import type { CellClickedEvent } from 'ag-grid-community';
import type { RowClickedEvent } from 'ag-grid-enterprise';
import { useCallback } from 'react';

import useDeviceQueryParam, {
  useDeviceSelectionParams
} from '@/pages/Administration/Devices/Devices.hooks';

import type { CloudSourceTreeNode } from '../CloudSourcesTable.types';

/**
 * Custom hook for managing row highlighting and URL synchronization.
 *
 * Synchronizes the selected source with URL query parameters and applies visual
 * highlighting to the selected row. Handles navigation to source detail pane when
 * a row is clicked.
 *
 * Business Logic:
 * - Reads `suid` (source UID) from URL query parameters
 * - Applies CSS class to matching row for visual highlight
 * - Opens detail pane when row is clicked (excluding button clicks)
 * - Prevents action menu clicks from triggering row selection
 *
 * @returns Object containing:
 * - `selectedSourceUid` - Currently selected source UID from URL (empty string if none)
 * - `handleRowClick` - Row click handler for navigation
 * - `handleCellClicked` - Cell click handler to prevent button propagation
 *
 * @example
 * ```tsx
 * const highlighting = useCloudSourcesHighlighting();
 * <AgGridReact
 *   onRowClicked={highlighting.handleRowClick}
 *   onCellClicked={highlighting.handleCellClicked}
 * />
 * ```
 */
export const useCloudSourcesHighlighting = () => {
  const { addSourceFilter } = useDeviceQueryParam();
  const { sourceUid } = useDeviceSelectionParams();

  const handleRowClick = useCallback(
    (event: RowClickedEvent<CloudSourceTreeNode>) => {
      // Don't handle if clicking on a button or interactive element
      const target = event.event?.target as HTMLElement;
      if (target?.closest('button, .MuiIconButton-root, .action-btn')) {
        return;
      }

      // Only navigate if clicking on a source (not provider group)
      const node = event.data;
      if (!node) {
        return;
      }

      // Open detail panel for the clicked row
      if (node.type === 'source' && node.sourceData) {
        addSourceFilter(node.sourceData.device_uid, node.sourceData.source_uid);
      } else if (node.type === 'provider' && node.providerData) {
        addSourceFilter(
          node.providerData.device_uid,
          node.providerData.source_uid
        );
      }
    },
    [addSourceFilter]
  );

  const handleCellClicked = useCallback(
    (event: CellClickedEvent<CloudSourceTreeNode>) => {
      // Don't handle if clicking on a button or interactive element
      const target = event.event?.target as HTMLElement;
      if (target?.closest('button, .MuiIconButton-root, .action-btn')) {
        event.event?.stopPropagation();
        event.event?.preventDefault();
        return;
      }
    },
    []
  );

  return {
    selectedSourceUid: sourceUid,
    handleRowClick,
    handleCellClicked
  };
};
